import { __ } from "@wordpress/i18n";
import { useEffect } from "@wordpress/element";
import { useSettings } from "../../context/SettingsContext";
import {
    Toggle,
    SettingRow,
    SectionCard,
    ButtonGroup,
    NumberInput,
    ColorInput,
    SelectInput,
    TextInput,
    InfoBox,
} from "../ui/index";

const ARROW_ICONS = [
    {
        value: "chevron",
        label: (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
            </svg>
        ),
    },
    {
        value: "arrow",
        label: (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 5 5 12 12 19" />
            </svg>
        ),
    },
    {
        value: "caret",
        label: (
            <svg viewBox="0 0 24 24" width="18" height="18">
                <path d="M14 5L6 12L14 19Z" fill="currentColor" />
            </svg>
        ),
    },
    {
        value: "angle",
        label: (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 19L8 12L15 5" />
            </svg>
        ),
    },
    {
        value: "thin",
        label: (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
            </svg>
        ),
    },
];

const THUMB_TRIGGERS = [
    {
        value: "click",
        label: __("Click", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "hover",
        label: __("Hover", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const THUMB_SLIDE_TYPES = [
    {
        value: "row",
        label: __(
            "Slide by Row",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
    {
        value: "item",
        label: __(
            "Slide by Item",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
];

const THUMB_POSITIONS = [
    {
        value: "bottom",
        label: __("Bottom", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "top",
        label: __("Top", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "left",
        label: __("Left", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "right",
        label: __("Right", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const THUMB_LAYOUTS = [
    {
        value: "slider",
        label: __("Slider", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "grid",
        label: __("Grid", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const THUMB_ALIGNMENTS_HORIZONTAL = [
    {
        value: "left",
        label: __("Left", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "center",
        label: __("Center", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "right",
        label: __("Right", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const THUMB_ALIGNMENTS_VERTICAL = [
    {
        value: "left",
        label: __("Top", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "center",
        label: __("Center", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "right",
        label: __("Bottom", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const IMAGE_SIZES = [
    {
        value: "thumbnail",
        label: __(
            "Thumbnail (150×150)",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
    {
        value: "medium",
        label: __(
            "Medium (300×300)",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
    {
        value: "medium_large",
        label: __(
            "Medium Large (768px)",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
    {
        value: "large",
        label: __(
            "Large (1024px)",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
    {
        value: "woocommerce_thumbnail",
        label: __(
            "WC Thumbnail",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
    {
        value: "woocommerce_single",
        label: __("WC Single", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "woocommerce_gallery_thumbnail",
        label: __(
            "WC Gallery Thumbnail",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
    {
        value: "full",
        label: __(
            "Full (original)",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
];

const HEIGHT_MODES = [
    {
        value: "lock",
        label: __("Lock", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "adaptive",
        label: __("Adaptive", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "fixed",
        label: __("Fixed", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const IMG_FITS = [
    {
        value: "contain",
        label: __("Contain", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "cover",
        label: __("Cover", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const TRANSITIONS = [
    {
        value: "fade",
        label: __("Fade", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "slide",
        label: __("Slide", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "zoom",
        label: __("Zoom", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "none",
        label: __("None", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const SLIDE_DIRECTIONS = [
    {
        value: "horizontal",
        label: __(
            "Horizontal",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
    {
        value: "vertical",
        label: __("Vertical", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const PAGINATION_TYPES = [
    {
        value: "dots",
        label: __("Dots", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "progressbar",
        label: __(
            "Progress Bar",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
        premium: true,
    },
    {
        value: "count",
        label: __("Count", "wpbean-product-gallery-slider-for-woocommerce"),
        premium: true,
    },
];

const PAGINATION_POSITIONS = [
    {
        value: "bottom",
        label: __("Bottom", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "top",
        label: __("Top", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "left",
        label: __("Left", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "right",
        label: __("Right", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const GALLERY_TYPES = [
    {
        value: "thumbnail",
        label: __(
            "Thumbnail Gallery",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
    },
    {
        value: "hierarchy-grid",
        label: __(
            "Hierarchy Grid",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
        premium: true,
    },
    {
        value: "anchor",
        label: __(
            "Anchor Navigation",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
        premium: true,
    },
    {
        value: "sticky-split",
        label: __(
            "Sticky Split",
            "wpbean-product-gallery-slider-for-woocommerce",
        ),
        premium: true,
    },
    {
        value: "tiles",
        label: __("Tiles", "wpbean-product-gallery-slider-for-woocommerce"),
        premium: true,
    },
];

const THUMB_ASPECTS = [
    {
        value: "auto",
        label: __("Auto", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    { value: "1/1", label: "1:1" },
    { value: "4/3", label: "4:3" },
    { value: "3/2", label: "3:2" },
    { value: "3/4", label: "3:4" },
    { value: "2/3", label: "2:3" },
];

const TILES_ITEM_ASPECTS = [
    {
        value: "auto",
        label: __("Auto", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    { value: "1/1", label: "1:1" },
    { value: "4/3", label: "4:3" },
    { value: "3/2", label: "3:2" },
    { value: "16/9", label: "16:9" },
];

const SS_MAIN_SIDES = [
    {
        value: "left",
        label: __("Left", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "right",
        label: __("Right", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const SS_ITEM_ASPECTS = [
    {
        value: "auto",
        label: __("Auto", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    { value: "1/1", label: "1:1" },
    { value: "4/3", label: "4:3" },
    { value: "3/2", label: "3:2" },
    { value: "16/9", label: "16:9" },
];

const SS_CLICK_ACTIONS = [
    {
        value: "lightbox",
        label: __("Lightbox", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    {
        value: "none",
        label: __("None", "wpbean-product-gallery-slider-for-woocommerce"),
    },
];

const HG_ITEM_ASPECTS = [
    {
        value: "auto",
        label: __("Auto", "wpbean-product-gallery-slider-for-woocommerce"),
    },
    { value: "1/1", label: "1:1" },
    { value: "4/3", label: "4:3" },
    { value: "3/2", label: "3:2" },
    { value: "16/9", label: "16:9" },
];

// ── General sub-panel ─────────────────────────────────────────────────────────

function GeneralSubPanel({ settings, s, actions, gallerySubTab }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title={__(
                    "Gallery Type",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Choose the overall gallery layout. Thumbnail Gallery shows images alongside a strip of clickable thumbnails. Anchor Navigation stacks all images vertically with dot navigation. Sticky Split shows a sticky main viewer alongside a vertically scrollable column of full-size images.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Gallery Type",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Sets the primary display mode for the product gallery.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    className="flex flex-col items-start gap-2"
                >
                    <ButtonGroup
                        options={GALLERY_TYPES}
                        value={settings.galleryType}
                        className="grid grid-cols-2 wpsm:inline-flex wpsm:items-center"
                        onChange={(v) => {
                            s("galleryType", v);
                            if (v === "anchor") {
                                if (
                                    ["top", "bottom"].includes(
                                        settings.thumbnailPosition,
                                    )
                                ) {
                                    s("thumbnailPosition", "left");
                                }
                                // anchor only supports left/right position
                                if (
                                    !["left", "right"].includes(
                                        settings.mainImagePaginationPosition,
                                    )
                                ) {
                                    s("mainImagePaginationPosition", "left");
                                }
                                // anchor dots always require "dots" type
                                if (
                                    settings.mainImagePaginationType !== "dots"
                                ) {
                                    s("mainImagePaginationType", "dots");
                                }
                                if (gallerySubTab === "transition") {
                                    actions.setGallerySubTab("general");
                                }
                            }
                            if (v !== "anchor") {
                                // count/progressbar only support top/bottom; reset if leaving anchor with left/right
                                if (
                                    (settings.mainImagePaginationType ===
                                        "count" ||
                                        settings.mainImagePaginationType ===
                                            "progressbar") &&
                                    ["left", "right"].includes(
                                        settings.mainImagePaginationPosition,
                                    )
                                ) {
                                    s("mainImagePaginationPosition", "bottom");
                                }
                            }
                            if (v === "sticky-split") {
                                if (gallerySubTab === "transition") {
                                    actions.setGallerySubTab("general");
                                }
                            }
                            if (v === "hierarchy-grid") {
                                if (gallerySubTab === "transition") {
                                    actions.setGallerySubTab("general");
                                }
                            }
                            if (v === "tiles") {
                                if (
                                    [
                                        "transition",
                                        "thumbnails",
                                        "main-image",
                                        "navigation",
                                    ].includes(gallerySubTab)
                                ) {
                                    actions.setGallerySubTab("general");
                                }
                            }
                            if (v !== "tiles") {
                                // If switching away from tiles while on tiles sub-tab, go back to general
                                if (gallerySubTab === "tiles") {
                                    actions.setGallerySubTab("general");
                                }
                            }
                        }}
                    />
                </SettingRow>
            </SectionCard>
        </div>
    );
}

// ── Thumbnails sub-panel ──────────────────────────────────────────────────────

function ThumbnailsSubPanel({ settings, s }) {
    const showThumbs = settings.thumbnailShow !== false;
    const isSlider = settings.thumbnailLayout === "slider";
    const isGrid = settings.thumbnailLayout === "grid";
    const isSidePos = ["left", "right"].includes(settings.thumbnailPosition);

    return (
        <div className="space-y-5">
            <SectionCard
                title={__(
                    "Thumbnail Image Size",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Choose which WordPress-registered image size is loaded for thumbnail items.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Thumbnail Image",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Image size used for the thumbnail strip items.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <SelectInput
                        options={IMAGE_SIZES}
                        value={settings.thumbnailImageSize}
                        onChange={(v) => s("thumbnailImageSize", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__(
                    "Thumbnails",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Configure gallery thumbnail position and layout.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Show Thumbnails",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Display the thumbnail strip alongside the main product image.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <Toggle
                        value={settings.thumbnailShow !== false}
                        onChange={(v) => s("thumbnailShow", v)}
                    />
                </SettingRow>

                {showThumbs && (
                    <>
                        <SettingRow
                            label={__(
                                "Layout",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "How thumbnails are arranged.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <ButtonGroup
                                options={THUMB_LAYOUTS}
                                value={settings.thumbnailLayout}
                                onChange={(v) => s("thumbnailLayout", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Position",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={
                                settings.galleryType === "anchor"
                                    ? __(
                                          "Where thumbnails appear relative to the stacked images. Only left and right are supported for Anchor Navigation.",
                                          "wpbean-product-gallery-slider-for-woocommerce",
                                      )
                                    : __(
                                          "Where thumbnails appear relative to the main image.",
                                          "wpbean-product-gallery-slider-for-woocommerce",
                                      )
                            }
                        >
                            <ButtonGroup
                                options={
                                    settings.galleryType === "anchor"
                                        ? THUMB_POSITIONS.filter(
                                              (p) =>
                                                  p.value === "left" ||
                                                  p.value === "right",
                                          )
                                        : THUMB_POSITIONS
                                }
                                value={
                                    settings.galleryType === "anchor" &&
                                    settings.thumbnailPosition !== "left" &&
                                    settings.thumbnailPosition !== "right"
                                        ? "left"
                                        : settings.thumbnailPosition
                                }
                                onChange={(v) => s("thumbnailPosition", v)}
                            />
                        </SettingRow>
                        {settings.galleryType !== "anchor" && (
                            <>
                                <SettingRow
                                    label={__(
                                        "Alignment",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                    description={__(
                                        "Position of thumbnails within the strip when they don't fill the full area. Has no effect when Fit to Main Image is on or when thumbnails overflow and scroll.",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                >
                                    <ButtonGroup
                                        options={
                                            isSidePos
                                                ? THUMB_ALIGNMENTS_VERTICAL
                                                : THUMB_ALIGNMENTS_HORIZONTAL
                                        }
                                        value={settings.thumbnailAlignment}
                                        onChange={(v) =>
                                            s("thumbnailAlignment", v)
                                        }
                                    />
                                </SettingRow>

                                {isSidePos && (
                                    <SettingRow
                                        label={__(
                                            "Responsive Breakpoint",
                                            "wpbean-product-gallery-slider-for-woocommerce",
                                        )}
                                        description={__(
                                            "When the gallery width drops below this value, left/right thumbnails move to the bottom. Set to 0 to disable.",
                                            "wpbean-product-gallery-slider-for-woocommerce",
                                        )}
                                    >
                                        <NumberInput
                                            value={settings.thumbnailBreakpoint}
                                            onChange={(v) =>
                                                s("thumbnailBreakpoint", v)
                                            }
                                            min={0}
                                            max={2560}
                                            unit="px"
                                            width="w-24"
                                        />
                                    </SettingRow>
                                )}
                            </>
                        )}

                        <SettingRow
                            label={__(
                                "Trigger",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "How hovering or clicking a thumbnail swaps the main image.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <ButtonGroup
                                options={THUMB_TRIGGERS}
                                value={settings.thumbnailTrigger}
                                onChange={(v) => s("thumbnailTrigger", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Entrance Animation",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Thumbnails fade and slide in with a staggered delay when the gallery first loads.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <Toggle
                                value={settings.thumbnailAnimation}
                                onChange={(v) => s("thumbnailAnimation", v)}
                            />
                        </SettingRow>
                    </>
                )}
            </SectionCard>

            {showThumbs && (
                <>
                    <SectionCard
                        title={__(
                            "Thumbnail Size",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "Set the dimensions of individual thumbnail images.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <SettingRow
                            label={__(
                                "Width",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "CSS display width of each thumbnail. Does not affect the image file loaded.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <NumberInput
                                value={settings.thumbnailWidth}
                                onChange={(v) => s("thumbnailWidth", v)}
                                min={40}
                                max={300}
                                unit="px"
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Height",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "CSS display height of each thumbnail. Does not affect the image file loaded.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <NumberInput
                                value={settings.thumbnailHeight}
                                onChange={(v) => s("thumbnailHeight", v)}
                                min={40}
                                max={300}
                                unit="px"
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Aspect Ratio",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Force a fixed aspect ratio on thumbnail items. When set, the height is derived from the width and this ratio overrides the Height setting.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <ButtonGroup
                                options={THUMB_ASPECTS}
                                value={settings.thumbnailAspectRatio ?? "auto"}
                                onChange={(v) => s("thumbnailAspectRatio", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Gap",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Space between thumbnail items.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <NumberInput
                                value={settings.thumbnailGap}
                                onChange={(v) => s("thumbnailGap", v)}
                                min={0}
                                max={40}
                                unit="px"
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Border Radius",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Rounded corner radius for thumbnails.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <NumberInput
                                value={settings.thumbnailBorderRadius}
                                onChange={(v) => s("thumbnailBorderRadius", v)}
                                min={0}
                                max={50}
                                unit="px"
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Background Color",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Background color applied to each thumbnail item. When set, images use mix-blend-mode: darken so white backgrounds of product images blend away cleanly.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <ColorInput
                                value={settings.thumbnailBackground ?? ""}
                                onChange={(v) => s("thumbnailBackground", v)}
                            />
                        </SettingRow>

                        {isSidePos && !settings.thumbnailFitToImage && (
                            <InfoBox variant="tip">
                                <strong>
                                    {__(
                                        "Tip:",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                </strong>{" "}
                                {__(
                                    "With a Left/Right position, enabling",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}{" "}
                                <em>
                                    {__(
                                        "Fit to Main Image",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                </em>{" "}
                                {__(
                                    "is recommended — it scales thumbnails to fill the full height of the main image for a cleaner look.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            </InfoBox>
                        )}

                        <SettingRow
                            label={__(
                                "Fit to Main Image",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Stretch or shrink thumbnails to fill the full width (or height for left/right) of the main image area.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <Toggle
                                value={settings.thumbnailFitToImage}
                                onChange={(v) => s("thumbnailFitToImage", v)}
                            />
                        </SettingRow>
                    </SectionCard>

                    <SectionCard
                        title={__(
                            "Active Thumbnail",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "Styling applied to the currently selected thumbnail.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <SettingRow
                            label={__(
                                "Highlight Color",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Border color shown on the active thumbnail.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <ColorInput
                                value={settings.thumbnailActiveBorderColor}
                                onChange={(v) =>
                                    s("thumbnailActiveBorderColor", v)
                                }
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Highlight Width",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Thickness of the active thumbnail border.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <NumberInput
                                value={settings.thumbnailActiveBorderWidth}
                                onChange={(v) =>
                                    s("thumbnailActiveBorderWidth", v)
                                }
                                min={1}
                                max={10}
                                unit="px"
                            />
                        </SettingRow>
                    </SectionCard>

                    {isSlider && (
                        <SectionCard
                            title={__(
                                "Slider Options",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Settings specific to the slider layout.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <SettingRow
                                label={__(
                                    "Navigation Arrows",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Show previous/next arrow buttons on the thumbnail slider.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <Toggle
                                    value={settings.thumbnailSliderButtons}
                                    onChange={(v) =>
                                        s("thumbnailSliderButtons", v)
                                    }
                                />
                            </SettingRow>

                            {settings.thumbnailSliderButtons && (
                                <SettingRow
                                    label={__(
                                        "Overlay Arrows",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                    description={__(
                                        "Position arrows over the slider edges so thumbnails fill the full strip width.",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                >
                                    <Toggle
                                        value={settings.thumbnailArrowsOverlay}
                                        onChange={(v) =>
                                            s("thumbnailArrowsOverlay", v)
                                        }
                                    />
                                </SettingRow>
                            )}

                            <SettingRow
                                label={__(
                                    "Slide Type",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Slide by Row moves a full visible page at once. Slide by Item moves one thumbnail at a time.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <ButtonGroup
                                    options={THUMB_SLIDE_TYPES}
                                    value={settings.thumbnailSlideType}
                                    onChange={(v) => s("thumbnailSlideType", v)}
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Loop",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Allow the slider to wrap around from the last thumbnail back to the first, and vice versa. The wrap-around uses a distinct fade animation.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <Toggle
                                    value={settings.thumbnailLoop}
                                    onChange={(v) => s("thumbnailLoop", v)}
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Drag Navigation",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Click-and-drag or touch-drag to scroll through thumbnails.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <Toggle
                                    value={settings.thumbnailDragNav !== false}
                                    onChange={(v) => s("thumbnailDragNav", v)}
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Scroll Navigation",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Use the mouse wheel or trackpad to scroll through thumbnails. Scrolls vertically for left/right strips and horizontally for top/bottom strips.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <Toggle
                                    value={
                                        settings.thumbnailScrollNav !== false
                                    }
                                    onChange={(v) => s("thumbnailScrollNav", v)}
                                />
                            </SettingRow>
                        </SectionCard>
                    )}

                    {isGrid && settings.galleryType !== "anchor" && (
                        <SectionCard
                            title={__(
                                "Grid Options",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Settings specific to the grid layout.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <SettingRow
                                label={__(
                                    "Columns",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Number of columns in the thumbnail grid.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <NumberInput
                                    value={settings.thumbnailGridColumns}
                                    onChange={(v) =>
                                        s("thumbnailGridColumns", v)
                                    }
                                    min={1}
                                    max={8}
                                />
                            </SettingRow>
                        </SectionCard>
                    )}
                </>
            )}
        </div>
    );
}

// ── Main Image sub-panel ──────────────────────────────────────────────────────

function MainImageSubPanel({ settings, s }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title={__(
                    "Image File Size",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Choose which WordPress-registered image size is served for the main product image. Larger sizes are sharper but slower.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Main Image",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Image size used for the main product image display.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <SelectInput
                        options={IMAGE_SIZES}
                        value={settings.mainImageSize}
                        onChange={(v) => s("mainImageSize", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__(
                    "Main Image",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Control the main product image display area.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                {settings.galleryType !== "anchor" && (
                    <SettingRow
                        label={__(
                            "Height Mode",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "Lock freezes the height after the first item loads — prevents layout shift. Adaptive grows the container to fit the tallest item seen, never shrinking. Fixed sets an explicit pixel height.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <ButtonGroup
                            options={HEIGHT_MODES}
                            value={settings.mainImageHeightMode}
                            onChange={(v) => s("mainImageHeightMode", v)}
                        />
                    </SettingRow>
                )}

                {settings.mainImageHeightMode === "fixed" && (
                    <SettingRow
                        label={__(
                            "Fixed Height",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "Explicit height for the main image area in pixels.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <NumberInput
                            value={settings.mainImageHeight || 0}
                            onChange={(v) => s("mainImageHeight", v || 0)}
                            min={100}
                            max={1200}
                            unit="px"
                            width="w-24"
                        />
                    </SettingRow>
                )}

                {settings.galleryType !== "anchor" && (
                    <SettingRow
                        label={__(
                            "Image Fit",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "How the image fills the display area.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <ButtonGroup
                            options={IMG_FITS}
                            value={settings.mainImageFit}
                            onChange={(v) => s("mainImageFit", v)}
                        />
                    </SettingRow>
                )}

                <SettingRow
                    label={__(
                        "Background Color",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Background shown behind the image.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ColorInput
                        value={settings.mainImageBackground}
                        onChange={(v) => s("mainImageBackground", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Strip Gap",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Space between the thumbnail strip and the main image area. Independent of the gap between individual thumbnail items.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.thumbnailStripGap}
                        onChange={(v) => s("thumbnailStripGap", v)}
                        min={0}
                        max={80}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Border Radius",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Rounded corner radius of the main image and its container.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.mainImageBorderRadius}
                        onChange={(v) => s("mainImageBorderRadius", v)}
                        min={0}
                        max={80}
                        unit="px"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Border Width",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Thickness of the border around the main image area.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.mainImageBorderWidth}
                        onChange={(v) => s("mainImageBorderWidth", v)}
                        min={0}
                        max={10}
                        unit="px"
                    />
                </SettingRow>

                {settings.mainImageBorderWidth > 0 && (
                    <SettingRow
                        label={__(
                            "Border Color",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <ColorInput
                            value={settings.mainImageBorderColor}
                            onChange={(v) => s("mainImageBorderColor", v)}
                        />
                    </SettingRow>
                )}

                <SettingRow
                    label={__(
                        "Drop Shadow",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Apply a soft shadow around the main image area.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <Toggle
                        value={settings.mainImageShadow}
                        onChange={(v) => s("mainImageShadow", v)}
                    />
                </SettingRow>
            </SectionCard>
        </div>
    );
}

// ── Navigation sub-panel ─────────────────────────────────────────────────────

function NavigationSubPanel({ settings, s, actions }) {
    // Write back a corrected position whenever galleryType or pagination type
    // makes the stored mainImagePaginationPosition invalid (e.g. "bottom" is
    // invalid for anchor; "left"/"right" are invalid for count/progressbar).
    useEffect(() => {
        if (!settings.mainImagePagination) return;
        const isTopBottomOnly =
            settings.mainImagePaginationType === "count" ||
            settings.mainImagePaginationType === "progressbar";
        const posOpts =
            settings.galleryType === "anchor"
                ? PAGINATION_POSITIONS.filter(
                      (p) => p.value === "left" || p.value === "right",
                  )
                : isTopBottomOnly
                ? PAGINATION_POSITIONS.filter(
                      (p) => p.value === "bottom" || p.value === "top",
                  )
                : PAGINATION_POSITIONS;
        const isValid = posOpts.some(
            (p) => p.value === settings.mainImagePaginationPosition,
        );
        if (!isValid && posOpts.length > 0) {
            s("mainImagePaginationPosition", posOpts[0].value);
        }
    }, [
        settings.galleryType,
        settings.mainImagePaginationType,
        settings.mainImagePagination,
        settings.mainImagePaginationPosition,
    ]);

    const isAnchor = settings.galleryType === "anchor";
    const isStickySplit = settings.galleryType === "sticky-split";
    const isThumbnail = settings.galleryType === "thumbnail";
    const isDots = settings.mainImagePaginationType === "dots";

    return (
        <div className="space-y-5">
            {/* Sticky-split advisory — shown before all cards */}
            {isStickySplit &&
                (settings.mainImageArrows || settings.mainImagePagination) && (
                    <InfoBox variant="tip">
                        {__(
                            "With",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}{" "}
                        <strong>
                            {__(
                                "Sticky Split",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        </strong>
                        {", "}
                        {__(
                            "the scroll column acts as the primary navigation. It is recommended to keep",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}{" "}
                        <strong>
                            {__(
                                "Navigation Arrows",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        </strong>{" "}
                        {__(
                            "and",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}{" "}
                        <strong>
                            {__(
                                "Pagination",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        </strong>{" "}
                        {__(
                            "disabled.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    </InfoBox>
                )}

            {/* ── Navigation Arrows ── */}
            {!isAnchor && (
                <SectionCard
                    title={__(
                        "Navigation Arrows",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Previous/next arrow buttons overlaid on the main image.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <SettingRow
                        label={__(
                            "Show Arrows",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "Show previous/next arrow buttons on the main image. Syncs with the thumbnail strip.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <Toggle
                            value={settings.mainImageArrows}
                            onChange={(v) => s("mainImageArrows", v)}
                        />
                    </SettingRow>

                    {settings.mainImageArrows && (
                        <>
                            <SettingRow
                                label={__(
                                    "Auto-hide",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Hide arrows when the cursor leaves the image. Turn off to keep them always visible.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <Toggle
                                    value={settings.mainImageArrowsAutoHide}
                                    onChange={(v) =>
                                        s("mainImageArrowsAutoHide", v)
                                    }
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Icon Style",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Choose the shape of the arrow icon.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <ButtonGroup
                                    options={ARROW_ICONS}
                                    value={settings.mainImageArrowIcon ?? "chevron"}
                                    onChange={(v) =>
                                        s("mainImageArrowIcon", v)
                                    }
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Size",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Diameter of the arrow buttons.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <NumberInput
                                    value={settings.mainImageArrowSize ?? 40}
                                    onChange={(v) =>
                                        s("mainImageArrowSize", v)
                                    }
                                    min={24}
                                    max={80}
                                    unit="px"
                                    width="w-24"
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Border Radius",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Corner radius of the arrow buttons. 50% gives a circle, 0% gives a square.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <NumberInput
                                    value={settings.mainImageArrowBorderRadius ?? 50}
                                    onChange={(v) =>
                                        s("mainImageArrowBorderRadius", v)
                                    }
                                    min={0}
                                    max={50}
                                    unit="%"
                                    width="w-24"
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Icon Size",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Size of the arrow SVG icon inside the button.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <NumberInput
                                    value={settings.mainImageArrowIconSize ?? 18}
                                    onChange={(v) =>
                                        s("mainImageArrowIconSize", v)
                                    }
                                    min={8}
                                    max={40}
                                    unit="px"
                                    width="w-24"
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Background",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Button background color in normal state.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <ColorInput
                                    value={settings.mainImageArrowBg ?? "rgba(255,255,255,0.92)"}
                                    onChange={(v) => s("mainImageArrowBg", v)}
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Icon Color",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Arrow icon color in normal state.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <ColorInput
                                    value={settings.mainImageArrowColor ?? "#1a1a1a"}
                                    onChange={(v) => s("mainImageArrowColor", v)}
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Hover Background",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Button background color on hover.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <ColorInput
                                    value={settings.mainImageArrowHoverBg ?? "rgba(0,0,0,0.82)"}
                                    onChange={(v) =>
                                        s("mainImageArrowHoverBg", v)
                                    }
                                />
                            </SettingRow>

                            <SettingRow
                                label={__(
                                    "Hover Icon Color",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Arrow icon color on hover.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <ColorInput
                                    value={settings.mainImageArrowHoverColor ?? "#ffffff"}
                                    onChange={(v) =>
                                        s("mainImageArrowHoverColor", v)
                                    }
                                />
                            </SettingRow>
                        </>
                    )}
                </SectionCard>
            )}

            {/* ── Pagination ── */}
            <SectionCard
                title={__(
                    "Pagination",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Dot, progress bar, or count indicators for the main image.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                {/* Anchor nav: dot position is always relevant */}
                {isAnchor && (
                    <SettingRow
                        label={__(
                            "Dot Position",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "Which side the scroll-linked navigation dots appear on.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <ButtonGroup
                            options={PAGINATION_POSITIONS.filter(
                                (p) =>
                                    p.value === "left" || p.value === "right",
                            )}
                            value={
                                ["left", "right"].includes(
                                    settings.mainImagePaginationPosition,
                                )
                                    ? settings.mainImagePaginationPosition
                                    : "left"
                            }
                            onChange={(v) =>
                                s("mainImagePaginationPosition", v)
                            }
                        />
                    </SettingRow>
                )}

                {!isAnchor && (
                    <>
                        <SettingRow
                            label={__(
                                "Show Pagination",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Show a pagination indicator for the main image for quick navigation.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <Toggle
                                value={settings.mainImagePagination}
                                onChange={(v) => s("mainImagePagination", v)}
                            />
                        </SettingRow>

                        {settings.mainImagePagination && (
                            <>
                                <SettingRow
                                    label={__(
                                        "Style",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                    description={__(
                                        "Dots are clickable indicators. Progress Bar shows a thin fill line. Count shows a numeric counter (e.g. 2 / 8) with arrow buttons.",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                >
                                    <ButtonGroup
                                        options={PAGINATION_TYPES}
                                        value={settings.mainImagePaginationType}
                                        onChange={(v) => {
                                            s("mainImagePaginationType", v);
                                            if (
                                                (v === "count" ||
                                                    v === "progressbar") &&
                                                ["left", "right"].includes(
                                                    settings.mainImagePaginationPosition,
                                                )
                                            ) {
                                                s(
                                                    "mainImagePaginationPosition",
                                                    "bottom",
                                                );
                                            }
                                        }}
                                    />
                                </SettingRow>

                                {isDots && (
                                    <>
                                        <SettingRow
                                            label={__(
                                                "Dot Size",
                                                "wpbean-product-gallery-slider-for-woocommerce",
                                            )}
                                            description={__(
                                                "Diameter of each dot. The active dot stretches proportionally.",
                                                "wpbean-product-gallery-slider-for-woocommerce",
                                            )}
                                        >
                                            <NumberInput
                                                value={settings.mainImageDotSize ?? 5}
                                                onChange={(v) =>
                                                    s("mainImageDotSize", v)
                                                }
                                                min={3}
                                                max={16}
                                                unit="px"
                                                width="w-24"
                                            />
                                        </SettingRow>

                                        <SettingRow
                                            label={__(
                                                "Dot Color",
                                                "wpbean-product-gallery-slider-for-woocommerce",
                                            )}
                                            description={__(
                                                "Color of the inactive pagination dots.",
                                                "wpbean-product-gallery-slider-for-woocommerce",
                                            )}
                                        >
                                            <ColorInput
                                                value={settings.mainImageDotColor ?? "rgba(0,0,0,0.2)"}
                                                onChange={(v) =>
                                                    s("mainImageDotColor", v)
                                                }
                                            />
                                        </SettingRow>

                                        <SettingRow
                                            label={__(
                                                "Active Dot Color",
                                                "wpbean-product-gallery-slider-for-woocommerce",
                                            )}
                                            description={__(
                                                "Color of the currently active pagination dot.",
                                                "wpbean-product-gallery-slider-for-woocommerce",
                                            )}
                                        >
                                            <ColorInput
                                                value={settings.mainImageDotActiveColor ?? "rgba(0,0,0,0.82)"}
                                                onChange={(v) =>
                                                    s("mainImageDotActiveColor", v)
                                                }
                                            />
                                        </SettingRow>
                                    </>
                                )}

                                {/* Pagination position */}
                                {(() => {
                                    const isTopBottomOnly =
                                        settings.mainImagePaginationType ===
                                            "count" ||
                                        settings.mainImagePaginationType ===
                                            "progressbar";
                                    const posOpts = isTopBottomOnly
                                        ? PAGINATION_POSITIONS.filter(
                                              (p) =>
                                                  p.value === "bottom" ||
                                                  p.value === "top",
                                          )
                                        : PAGINATION_POSITIONS;
                                    const safePos = posOpts.some(
                                        (p) =>
                                            p.value ===
                                            settings.mainImagePaginationPosition,
                                    )
                                        ? settings.mainImagePaginationPosition
                                        : posOpts[0]?.value;
                                    return (
                                        <SettingRow
                                            label={__(
                                                "Position",
                                                "wpbean-product-gallery-slider-for-woocommerce",
                                            )}
                                            description={__(
                                                "Where the pagination appears. Progress Bar and Count support Top or Bottom only.",
                                                "wpbean-product-gallery-slider-for-woocommerce",
                                            )}
                                        >
                                            <ButtonGroup
                                                options={posOpts}
                                                value={safePos}
                                                onChange={(v) =>
                                                    s(
                                                        "mainImagePaginationPosition",
                                                        v,
                                                    )
                                                }
                                            />
                                        </SettingRow>
                                    );
                                })()}

                                <SettingRow
                                    label={__(
                                        "Auto-hide",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                    description={__(
                                        "Hide pagination when the cursor leaves the image. Turn off to keep it always visible.",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                >
                                    <Toggle
                                        value={settings.mainImagePaginationAutoHide}
                                        onChange={(v) =>
                                            s("mainImagePaginationAutoHide", v)
                                        }
                                    />
                                </SettingRow>
                            </>
                        )}
                    </>
                )}
            </SectionCard>

            {/* ── Swipe & Scroll ── */}
            {(isThumbnail || isStickySplit) && (
                <SectionCard
                    title={__(
                        "Swipe & Scroll",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Gesture and scroll-wheel navigation for the main image.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    {!settings.zoomOnHover && !settings.touchZoom && (
                        <SettingRow
                            label={__(
                                "Swipe / Drag Navigation",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Drag or swipe the main image to navigate between gallery items. Only available when Zoom on Hover and Touch Zoom are both disabled.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <Toggle
                                value={settings.mainImageSwipe}
                                onChange={(v) => s("mainImageSwipe", v)}
                            />
                        </SettingRow>
                    )}

                    {isThumbnail && (
                        <>
                            <SettingRow
                                label={__(
                                    "Scroll Navigation",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                                description={__(
                                    "Use the mouse scroll wheel to navigate between gallery images.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            >
                                <Toggle
                                    value={settings.mainImageScrollNav}
                                    onChange={(v) =>
                                        s("mainImageScrollNav", v)
                                    }
                                />
                            </SettingRow>

                            {settings.mainImageScrollNav &&
                                settings.zoomOnHover && (
                                    <InfoBox variant="tip">
                                        <strong>
                                            {__(
                                                "Note:",
                                                "wpbean-product-gallery-slider-for-woocommerce",
                                            )}
                                        </strong>{" "}
                                        {__(
                                            "Scroll Navigation works best when",
                                            "wpbean-product-gallery-slider-for-woocommerce",
                                        )}{" "}
                                        <strong>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    actions.setTab("zoom")
                                                }
                                            >
                                                {__(
                                                    "Zoom on Hover",
                                                    "wpbean-product-gallery-slider-for-woocommerce",
                                                )}
                                            </button>
                                        </strong>{" "}
                                        {__(
                                            "is disabled — enabling both at the same time could cause interference.",
                                            "wpbean-product-gallery-slider-for-woocommerce",
                                        )}
                                    </InfoBox>
                                )}

                            {settings.mainImageScrollNav && (
                                <SettingRow
                                    label={__(
                                        "Scroll Response Speed",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                    description={__(
                                        "Minimum time between scroll-triggered navigations. Higher values slow down how fast scrolling moves through images.",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                >
                                    <NumberInput
                                        value={
                                            settings.mainImageScrollNavDelay ??
                                            500
                                        }
                                        onChange={(v) =>
                                            s("mainImageScrollNavDelay", v)
                                        }
                                        min={100}
                                        max={2000}
                                        unit="ms"
                                        width="w-24"
                                    />
                                </SettingRow>
                            )}
                        </>
                    )}
                </SectionCard>
            )}

            {/* ── Lightbox Button ── */}
            {settings.lightbox && settings.lightboxOnClick && (
                <SectionCard
                    title={__(
                        "Lightbox Button",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "The expand button that opens the full-screen lightbox viewer.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <SettingRow
                        label={__(
                            "Auto-hide Expand Button",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "Hide the lightbox expand button when the cursor leaves the image. Turn off to keep it always visible.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <Toggle
                            value={settings.lightboxButtonAutoHide}
                            onChange={(v) => s("lightboxButtonAutoHide", v)}
                        />
                    </SettingRow>
                </SectionCard>
            )}
        </div>
    );
}

// ── Transition sub-panel ─────────────────────────────────────────────────────

function TransitionSubPanel({ settings, s }) {
    const isSidePos = ["left", "right"].includes(settings.thumbnailPosition);

    return (
        <div className="space-y-5">
            <SectionCard
                title={__(
                    "Image Transition",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Animation when switching between gallery images.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Effect",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ButtonGroup
                        options={TRANSITIONS}
                        value={settings.mainImageTransition}
                        onChange={(v) => s("mainImageTransition", v)}
                    />
                </SettingRow>

                {settings.mainImageTransition === "slide" && (
                    <>
                        <SettingRow
                            label={__(
                                "Slide Direction",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Direction images slide when navigating between gallery items.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <ButtonGroup
                                options={SLIDE_DIRECTIONS}
                                value={settings.mainImageSlideDirection}
                                onChange={(v) =>
                                    s("mainImageSlideDirection", v)
                                }
                            />
                        </SettingRow>

                        {isSidePos &&
                            settings.mainImageSlideDirection !== "vertical" && (
                                <InfoBox>
                                    <strong>
                                        {__(
                                            "Tip:",
                                            "wpbean-product-gallery-slider-for-woocommerce",
                                        )}
                                    </strong>{" "}
                                    {__(
                                        "With a Left/Right thumbnail position, setting the slide direction to",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}{" "}
                                    <em>
                                        {__(
                                            "Vertical",
                                            "wpbean-product-gallery-slider-for-woocommerce",
                                        )}
                                    </em>{" "}
                                    {__(
                                        "complements the side-by-side layout better.",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                </InfoBox>
                            )}

                        {!isSidePos &&
                            settings.mainImageSlideDirection !==
                                "horizontal" && (
                                <InfoBox>
                                    <strong>
                                        {__(
                                            "Tip:",
                                            "wpbean-product-gallery-slider-for-woocommerce",
                                        )}
                                    </strong>{" "}
                                    {__(
                                        "With a Top/Bottom thumbnail position, setting the slide direction to",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}{" "}
                                    <em>
                                        {__(
                                            "Horizontal",
                                            "wpbean-product-gallery-slider-for-woocommerce",
                                        )}
                                    </em>{" "}
                                    {__(
                                        "complements the stacked layout better.",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                </InfoBox>
                            )}
                    </>
                )}

                {settings.mainImageTransition !== "none" && (
                    <SettingRow
                        label={__(
                            "Duration",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "How long the transition animation takes.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <NumberInput
                            value={settings.mainImageTransitionDuration}
                            onChange={(v) =>
                                s("mainImageTransitionDuration", v)
                            }
                            min={50}
                            max={2000}
                            unit="ms"
                            width="w-24"
                        />
                    </SettingRow>
                )}
            </SectionCard>
        </div>
    );
}

// ── Stacked Layout sub-panel ──────────────────────────────────────────────────

function StackedSubPanel({ settings, s, actions }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title={__(
                    "Stacked Layout",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Customise the Anchor Navigation (stacked) gallery layout and its dot pagination.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Item Gap",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Vertical space between each stacked gallery item.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.stackedItemGap}
                        onChange={(v) => s("stackedItemGap", v)}
                        min={0}
                        max={120}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Navigation Type",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Navigation type for the Stacked Layout. Dot Navigation places clickable dots on the side for navigation. Thumbnails shows a vertical strip of thumbnail images instead.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ButtonGroup
                        options={[
                            {
                                value: "dots",
                                label: __(
                                    "Dot",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                ),
                            },
                            {
                                value: "thumbs",
                                label: __(
                                    "Thumbnail",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                ),
                            },
                        ]}
                        value={settings.stackedNavigationType}
                        onChange={(v) => s("stackedNavigationType", v)}
                    />
                </SettingRow>

                {settings.stackedNavigationType === "thumbs" && (
                    <SettingRow
                        label={__(
                            "Sticky Offset",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "Distance from the top of the viewport when the thumbnail strip is sticky (left/right positions only).",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <NumberInput
                            value={settings.stackedThumbsStickyTop}
                            onChange={(v) => s("stackedThumbsStickyTop", v)}
                            min={0}
                            max={500}
                            unit="px"
                            width="w-24"
                        />
                    </SettingRow>
                )}

                {settings.stackedNavigationType === "thumbs" && (
                    <InfoBox className="mt-2 mb-2">
                        {__(
                            "Go to the",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}{" "}
                        <button
                            type="button"
                            onClick={() =>
                                actions.setGallerySubTab("thumbnails")
                            }
                            className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
                        >
                            {__(
                                "Thumbnails tab",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        </button>{" "}
                        {__(
                            "and adjust the thumbnail settings there to style the thumbnail navigation for the stacked layout.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    </InfoBox>
                )}

                {settings.stackedNavigationType === "dots" && (
                    <>
                        <SettingRow
                            label={__(
                                "Dot Navigation",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Overlay dots float on top of the images. Outside places them in a separate column beside the gallery.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <ButtonGroup
                                options={[
                                    {
                                        value: "overlay",
                                        label: __(
                                            "Overlay",
                                            "wpbean-product-gallery-slider-for-woocommerce",
                                        ),
                                    },
                                    {
                                        value: "outside",
                                        label: __(
                                            "Outside",
                                            "wpbean-product-gallery-slider-for-woocommerce",
                                        ),
                                    },
                                ]}
                                value={settings.stackedDotsPosition}
                                onChange={(v) => s("stackedDotsPosition", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Dot Gap",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Space between the pagination dots.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <NumberInput
                                value={settings.stackedDotGap}
                                onChange={(v) => s("stackedDotGap", v)}
                                min={2}
                                max={30}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Dot Size",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Diameter of each pagination dot.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <NumberInput
                                value={settings.stackedDotSize}
                                onChange={(v) => s("stackedDotSize", v)}
                                min={4}
                                max={24}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Dot Color",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Color of the inactive pagination dots.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <ColorInput
                                value={settings.stackedDotColor}
                                onChange={(v) => s("stackedDotColor", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Active Dot Color",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Color of the currently active pagination dot.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <ColorInput
                                value={settings.stackedDotActiveColor}
                                onChange={(v) => s("stackedDotActiveColor", v)}
                            />
                        </SettingRow>
                    </>
                )}
            </SectionCard>

            <SectionCard
                title={__(
                    "Zoom",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Hover zoom for the stacked images. Uses the zoom type and settings from the Zoom tab.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Zoom on Hover",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Enable zoom when hovering over each image in the stacked gallery. Requires Zoom on Hover to be enabled in the Zoom tab.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <Toggle
                        value={settings.anchorZoom !== false}
                        onChange={(v) => s("anchorZoom", v)}
                    />
                </SettingRow>

                {settings.anchorZoom !== false && !settings.zoomOnHover && (
                    <InfoBox variant="tip">
                        <strong>
                            {__(
                                "Zoom on Hover",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        </strong>{" "}
                        {__(
                            "is currently disabled in the Zoom tab. Enable it there for this setting to take effect.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    </InfoBox>
                )}
            </SectionCard>
        </div>
    );
}

// ── Hierarchy Grid sub-panel ──────────────────────────────────────────────────

function HierarchyGridSubPanel({ settings, s }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title={__(
                    "Grid Layout",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Configure the hierarchy grid below the hero image. The first gallery image is always displayed full-width; remaining images fill a multi-column grid.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Grid Columns",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Number of columns in the grid below the hero image.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.hierarchyGridColumns}
                        onChange={(v) => s("hierarchyGridColumns", v)}
                        min={1}
                        max={6}
                        width="w-20"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Gap",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Space between the hero image and grid items, and between grid items.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.hierarchyGridGap}
                        onChange={(v) => s("hierarchyGridGap", v)}
                        min={0}
                        max={80}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Item Border Radius",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Rounded corner radius applied to each grid item.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.hierarchyGridItemBorderRadius}
                        onChange={(v) => s("hierarchyGridItemBorderRadius", v)}
                        min={0}
                        max={60}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__(
                    "Grid Item Style",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Control how images inside the grid cells are sized and fitted.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Aspect Ratio",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Enforce a fixed aspect ratio on grid items. Auto uses each image's natural dimensions.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ButtonGroup
                        options={HG_ITEM_ASPECTS}
                        value={settings.hierarchyGridItemAspect}
                        onChange={(v) => s("hierarchyGridItemAspect", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Image Fit",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "How images fill the grid item area when an aspect ratio is enforced.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ButtonGroup
                        options={IMG_FITS}
                        value={settings.hierarchyGridItemFit}
                        onChange={(v) => s("hierarchyGridItemFit", v)}
                    />
                </SettingRow>
            </SectionCard>

            {settings.zoomOnHover !== false && (
                <SectionCard
                    title={__(
                        "Zoom",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Hover zoom for individual images in the hierarchy grid. Uses the zoom type and settings from the Zoom tab.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <SettingRow
                        label={__(
                            "Zoom on Hover",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        description={__(
                            "Enable zoom when hovering over each image in the grid. Requires Zoom on Hover to be enabled in the Zoom tab.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    >
                        <Toggle
                            value={settings.hierarchyGridZoom !== false}
                            onChange={(v) => s("hierarchyGridZoom", v)}
                        />
                    </SettingRow>

                    {settings.hierarchyGridZoom !== false &&
                        !settings.zoomOnHover && (
                            <InfoBox variant="tip">
                                <strong>
                                    {__(
                                        "Zoom on Hover",
                                        "wpbean-product-gallery-slider-for-woocommerce",
                                    )}
                                </strong>{" "}
                                {__(
                                    "is currently disabled in the Zoom tab. Enable it there for this setting to take effect.",
                                    "wpbean-product-gallery-slider-for-woocommerce",
                                )}
                            </InfoBox>
                        )}
                </SectionCard>
            )}
        </div>
    );
}

// ── Sticky Split sub-panel ────────────────────────────────────────────────────

function StickySplitSubPanel({ settings, s }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title={__(
                    "Layout",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Configure the two-column sticky split layout. The sticky column holds the main image viewer; the scroll column shows all images full-size.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Main Side",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Which column holds the sticky image viewer.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ButtonGroup
                        options={SS_MAIN_SIDES}
                        value={settings.stickySplitMainSide}
                        onChange={(v) => s("stickySplitMainSide", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Main Column Width",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Percentage of the total gallery width used by the sticky viewer column.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.stickySplitMainWidthPercent}
                        onChange={(v) => s("stickySplitMainWidthPercent", v)}
                        min={20}
                        max={80}
                        unit="%"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Column Gap",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Space between the sticky viewer column and the scroll column.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.stickySplitGap}
                        onChange={(v) => s("stickySplitGap", v)}
                        min={0}
                        max={120}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Sticky Top Offset",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Distance from the top of the viewport when the main viewer is in sticky position.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.stickySplitStickyTop}
                        onChange={(v) => s("stickySplitStickyTop", v)}
                        min={0}
                        max={300}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Click Sync",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "When enabled, clicking a scroll item also swaps the sticky viewer to that image. Independent of the lightbox click action.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <Toggle
                        value={settings.stickySplitClickSync}
                        onChange={(v) => s("stickySplitClickSync", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__(
                    "Scroll Column",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Style the full-size images in the scrollable column.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Item Gap",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Vertical space between images in the scroll column.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.stickySplitScrollItemGap}
                        onChange={(v) => s("stickySplitScrollItemGap", v)}
                        min={0}
                        max={120}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Aspect Ratio",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Enforce a fixed aspect ratio on scroll items. Auto uses each image's natural ratio.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ButtonGroup
                        options={SS_ITEM_ASPECTS}
                        value={settings.stickySplitScrollItemAspect}
                        onChange={(v) => s("stickySplitScrollItemAspect", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Image Fit",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "How images fill the scroll item area when an aspect ratio is enforced.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ButtonGroup
                        options={IMG_FITS}
                        value={settings.stickySplitScrollItemFit}
                        onChange={(v) => s("stickySplitScrollItemFit", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Border Radius",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Rounded corner radius applied to each scroll item.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.stickySplitScrollItemBorderRadius}
                        onChange={(v) =>
                            s("stickySplitScrollItemBorderRadius", v)
                        }
                        min={0}
                        max={60}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Include Main Image",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Show the main product image as the first item in the scroll column. When disabled, the scroll column starts from the second gallery image.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <Toggle
                        value={settings.stickySplitShowMainInScroll === true}
                        onChange={(v) => s("stickySplitShowMainInScroll", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Show Captions",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Display caption text below each scroll item.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <Toggle
                        value={settings.stickySplitShowCaption}
                        onChange={(v) => s("stickySplitShowCaption", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Zoom on Hover",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Enable zoom when hovering over images in the scroll column. Uses the same zoom type configured for the main viewer.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <Toggle
                        value={settings.stickySplitThumbZoom}
                        onChange={(v) => s("stickySplitThumbZoom", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__(
                    "Click Action",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "What happens when a scroll item is clicked.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "On Click",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Open the lightbox viewer, or disable click interaction.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ButtonGroup
                        options={SS_CLICK_ACTIONS}
                        value={settings.stickySplitClickAction}
                        onChange={(v) => s("stickySplitClickAction", v)}
                    />
                </SettingRow>
            </SectionCard>
        </div>
    );
}

// ── Tiles sub-panel ───────────────────────────────────────────────────────────

function TilesSubPanel({ settings, s }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title={__(
                    "Tiles Grid",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "All product images are displayed as equal-sized tiles in a grid. A Show More / Show Less button controls how many tiles are visible at once.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Columns",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Number of equal-width columns in the tiles grid.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.tilesColumns}
                        onChange={(v) => s("tilesColumns", v)}
                        min={1}
                        max={6}
                        width="w-20"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Gap",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Space between tiles.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.tilesGap}
                        onChange={(v) => s("tilesGap", v)}
                        min={0}
                        max={80}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Aspect Ratio",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Enforce a fixed aspect ratio on each tile. Auto uses each image's natural dimensions.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ButtonGroup
                        options={TILES_ITEM_ASPECTS}
                        value={settings.tilesItemAspect}
                        onChange={(v) => s("tilesItemAspect", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Image Fit",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "How images fill the tile area when an aspect ratio is enforced.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <ButtonGroup
                        options={IMG_FITS}
                        value={settings.tilesItemFit}
                        onChange={(v) => s("tilesItemFit", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__(
                        "Border Radius",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Rounded corner radius applied to each tile.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <NumberInput
                        value={settings.tilesItemBorderRadius}
                        onChange={(v) => s("tilesItemBorderRadius", v)}
                        min={0}
                        max={60}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__(
                    "Show More / Show Less",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Limit the initial number of visible tiles and let visitors reveal the rest with a button.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Show More Button",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Display a button at the bottom that reveals tiles beyond the initial count.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <Toggle
                        value={settings.tilesShowMoreEnabled !== false}
                        onChange={(v) => s("tilesShowMoreEnabled", v)}
                    />
                </SettingRow>

                {settings.tilesShowMoreEnabled !== false && (
                    <>
                        <SettingRow
                            label={__(
                                "Initial Count",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Number of tiles visible before the Show More button is clicked. Set to 0 to show all tiles.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <NumberInput
                                value={settings.tilesInitialCount}
                                onChange={(v) => s("tilesInitialCount", v)}
                                min={0}
                                max={100}
                                width="w-24"
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Show More Text",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Label on the button that reveals hidden tiles.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <TextInput
                                value={settings.tilesShowMoreText}
                                onChange={(v) => s("tilesShowMoreText", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__(
                                "Show Less Text",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                            description={__(
                                "Label on the button that collapses tiles back to the initial count.",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        >
                            <TextInput
                                value={settings.tilesShowLessText}
                                onChange={(v) => s("tilesShowLessText", v)}
                            />
                        </SettingRow>
                    </>
                )}
            </SectionCard>

            <SectionCard
                title={__(
                    "Zoom",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
                description={__(
                    "Hover zoom for individual tiles. Uses the zoom type and settings from the Zoom tab.",
                    "wpbean-product-gallery-slider-for-woocommerce",
                )}
            >
                <SettingRow
                    label={__(
                        "Zoom on Hover",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    description={__(
                        "Enable zoom when hovering over each tile. Requires Zoom on Hover to be enabled in the Zoom tab.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                >
                    <Toggle
                        value={settings.tilesZoom !== false}
                        onChange={(v) => s("tilesZoom", v)}
                    />
                </SettingRow>

                {settings.tilesZoom !== false && !settings.zoomOnHover && (
                    <InfoBox variant="tip">
                        <strong>
                            {__(
                                "Zoom on Hover",
                                "wpbean-product-gallery-slider-for-woocommerce",
                            )}
                        </strong>{" "}
                        {__(
                            "is currently disabled in the Zoom tab. Enable it there for this setting to take effect.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                    </InfoBox>
                )}
            </SectionCard>
        </div>
    );
}

// ── Root panel — routes to the active sub-panel ───────────────────────────────

export default function GalleryPanel() {
    const {
        state: { settings, gallerySubTab },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    switch (gallerySubTab) {
        case "general":
            return (
                <GeneralSubPanel
                    settings={settings}
                    s={s}
                    actions={actions}
                    gallerySubTab={gallerySubTab}
                />
            );
        case "main-image":
            return <MainImageSubPanel settings={settings} s={s} />;
        case "navigation":
            return (
                <NavigationSubPanel
                    settings={settings}
                    actions={actions}
                    s={s}
                />
            );
        case "transition":
            if (
                settings.galleryType === "anchor" ||
                settings.galleryType === "sticky-split" ||
                settings.galleryType === "hierarchy-grid" ||
                settings.galleryType === "tiles"
            ) {
                return (
                    <GeneralSubPanel
                        settings={settings}
                        s={s}
                        actions={actions}
                        gallerySubTab={gallerySubTab}
                    />
                );
            }
            return <TransitionSubPanel settings={settings} s={s} />;
        case "stacked":
            return (
                <StackedSubPanel settings={settings} s={s} actions={actions} />
            );
        case "sticky-split":
            return <StickySplitSubPanel settings={settings} s={s} />;
        case "hierarchy-grid":
            return <HierarchyGridSubPanel settings={settings} s={s} />;
        case "tiles":
            return <TilesSubPanel settings={settings} s={s} />;
        default:
            return <ThumbnailsSubPanel settings={settings} s={s} />;
    }
}
